import { AlertCircle, Bell, CheckCircle2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../components/LoadingSpinner';
import {
  type EmailNotificationPreferences,
  type EmailNotificationType,
  fetchEmailNotificationPreferences,
  listChangedPreferenceTypes,
  preferencesEqual,
  saveEmailNotificationPreferences,
} from '../../services/emailNotificationPreferences';

type FrequencyValue = 'off' | 'weekly' | 'daily' | 'on';

interface NotificationItem {
  type: EmailNotificationType;
  label: string;
  description?: string;
  frequencies: Array<{ value: FrequencyValue; label: string }>;
}

interface NotificationSection {
  id: string;
  title: string;
  description: string;
  items: NotificationItem[];
}

const UNSUB_ERROR_KEYS: Record<string, string> = {
  missing_token: 'views.accountSettings.notifications.unsub.missingToken',
  expired: 'views.accountSettings.notifications.unsub.expired',
  invalid: 'views.accountSettings.notifications.unsub.invalid',
  unavailable: 'views.accountSettings.notifications.unsub.unavailable',
  failed: 'views.accountSettings.notifications.unsub.failed',
};

const UNSUB_ERROR_DEFAULTS: Record<string, string> = {
  missing_token: 'The unsubscribe link was invalid.',
  expired: 'The unsubscribe link has expired.',
  invalid: 'The unsubscribe link was not valid.',
  unavailable: 'The unsubscribe service was temporarily unavailable.',
  failed: 'We could not save your preference from the email link.',
};

const TRANSACTIONAL_EMAIL_KEYS = [
  'views.accountSettings.notifications.transactional.welcome',
  'views.accountSettings.notifications.transactional.payment',
  'views.accountSettings.notifications.transactional.application',
  'views.accountSettings.notifications.transactional.applyLater',
  'views.accountSettings.notifications.transactional.support',
  'views.accountSettings.notifications.transactional.security',
] as const;

const TRANSACTIONAL_EMAIL_DEFAULTS = [
  'Welcome and account verification',
  'Payment receipts and billing updates',
  'Job application confirmations',
  'Apply-later reminders you set yourself',
  'Support ticket updates',
  'Security and password reset',
] as const;

function enabledToFrequency(item: NotificationItem, enabled: boolean): FrequencyValue {
  if (!enabled) return 'off';
  const nonOff = item.frequencies.find((f) => f.value !== 'off');
  return nonOff?.value ?? 'on';
}

function frequencyToEnabled(value: FrequencyValue): boolean {
  return value !== 'off';
}

function applyFrequencyChange(
  prefs: EmailNotificationPreferences,
  type: EmailNotificationType,
  value: FrequencyValue,
): EmailNotificationPreferences {
  return { ...prefs, [type]: frequencyToEnabled(value) };
}

export interface NotificationsSettingsPanelProps {
  highlightType?: EmailNotificationType | null;
  unsubError?: string | null;
}

const NotificationsSettingsPanel: React.FC<NotificationsSettingsPanelProps> = ({
  highlightType = null,
  unsubError = null,
}) => {
  const { t, i18n } = useTranslation();
  const [savedPrefs, setSavedPrefs] = useState<EmailNotificationPreferences | null>(null);
  const [draftPrefs, setDraftPrefs] = useState<EmailNotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const freq = useMemo(
    () => ({
      noEmail: t('views.accountSettings.notifications.frequency.noEmail', 'No email'),
      weekly: t('views.accountSettings.notifications.frequency.weekly', 'Weekly'),
      dailyDigest: t('views.accountSettings.notifications.frequency.dailyDigest', 'Daily digest'),
      whenPublished: t('views.accountSettings.notifications.frequency.whenPublished', 'When published'),
      sendTips: t('views.accountSettings.notifications.frequency.sendTips', 'Send tips'),
    }),
    [i18n.language],
  );

  const sections = useMemo((): NotificationSection[] => [
    {
      id: 'career',
      title: t('views.accountSettings.notifications.sections.career.title', 'Career content'),
      description: t(
        'views.accountSettings.notifications.sections.career.desc',
        'Career growth tips and weekly summaries to help you stay on track with your job search.',
      ),
      items: [
        {
          type: 'career_tips',
          label: t('views.accountSettings.notifications.items.career_tips.label', 'Weekly career tips'),
          description: t(
            'views.accountSettings.notifications.items.career_tips.desc',
            'Personalized tips sent every Monday based on your profile.',
          ),
          frequencies: [
            { value: 'off', label: freq.noEmail },
            { value: 'weekly', label: freq.weekly },
          ],
        },
        {
          type: 'weekly_digest',
          label: t('views.accountSettings.notifications.items.weekly_digest.label', 'Weekly progress digest'),
          description: t(
            'views.accountSettings.notifications.items.weekly_digest.desc',
            'Summary of your activity, matches, and suggested next steps.',
          ),
          frequencies: [
            { value: 'off', label: freq.noEmail },
            { value: 'weekly', label: freq.weekly },
          ],
        },
      ],
    },
    {
      id: 'jobs',
      title: t('views.accountSettings.notifications.sections.jobs.title', 'Job opportunities'),
      description: t(
        'views.accountSettings.notifications.sections.jobs.desc',
        'Emails about jobs matched to your profile and saved applications.',
      ),
      items: [
        {
          type: 'job_digest',
          label: t('views.accountSettings.notifications.items.job_digest.label', 'Job recommendations digest'),
          description: t(
            'views.accountSettings.notifications.items.job_digest.desc',
            'Curated roles matched to your profile when new jobs are available.',
          ),
          frequencies: [
            { value: 'off', label: freq.noEmail },
            { value: 'daily', label: freq.dailyDigest },
          ],
        },
        {
          type: 'optimization_reminder',
          label: t(
            'views.accountSettings.notifications.items.optimization_reminder.label',
            'Resume optimization reminders',
          ),
          description: t(
            'views.accountSettings.notifications.items.optimization_reminder.desc',
            'Nudges when your resume could be improved for better matches.',
          ),
          frequencies: [
            { value: 'off', label: freq.noEmail },
            { value: 'weekly', label: freq.weekly },
          ],
        },
      ],
    },
    {
      id: 'onboarding',
      title: t('views.accountSettings.notifications.sections.onboarding.title', 'Getting started'),
      description: t(
        'views.accountSettings.notifications.sections.onboarding.desc',
        'Helpful emails while you set up your GoGrabJob workspace.',
      ),
      items: [
        {
          type: 'onboarding_nudge',
          label: t('views.accountSettings.notifications.items.onboarding_nudge.label', 'Onboarding tips'),
          description: t(
            'views.accountSettings.notifications.items.onboarding_nudge.desc',
            'Short reminders to complete your profile and explore key features.',
          ),
          frequencies: [
            { value: 'off', label: freq.noEmail },
            { value: 'on', label: freq.sendTips },
          ],
        },
      ],
    },
    {
      id: 'blog',
      title: t('views.accountSettings.notifications.sections.blog.title', 'Blog & newsletter'),
      description: t(
        'views.accountSettings.notifications.sections.blog.desc',
        'Updates when we publish new articles on careers, interviews, and hiring trends.',
      ),
      items: [
        {
          type: 'blog',
          label: t('views.accountSettings.notifications.items.blog.label', 'Blog newsletter'),
          description: t(
            'views.accountSettings.notifications.items.blog.desc',
            'New posts and curated career content from the GoGrabJob blog.',
          ),
          frequencies: [
            { value: 'off', label: freq.noEmail },
            { value: 'weekly', label: freq.whenPublished },
          ],
        },
      ],
    },
  ], [i18n.language, freq]);

  const isDirty = useMemo(
    () => savedPrefs != null && draftPrefs != null && !preferencesEqual(savedPrefs, draftPrefs),
    [savedPrefs, draftPrefs],
  );

  const fetchPreferences = useCallback(async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading ?? false;
    if (showLoading) setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const data = await fetchEmailNotificationPreferences();
      setSavedPrefs(data);
      setDraftPrefs(data);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('views.accountSettings.notifications.loadError', 'Failed to load notification settings.'),
      );
      setSavedPrefs(null);
      setDraftPrefs(null);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setSuccessMsg(null);
      try {
        const data = await fetchEmailNotificationPreferences();
        if (!cancelled) {
          setSavedPrefs(data);
          setDraftPrefs(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : t('views.accountSettings.notifications.loadError', 'Failed to load notification settings.'),
          );
          setSavedPrefs(null);
          setDraftPrefs(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Fetch once on mount; language changes remount via parent key={i18n.language}.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!highlightType) return;
    const el = document.getElementById(`email-pref-${highlightType}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightType, loading]);

  const handleDraftChange = (type: EmailNotificationType, value: FrequencyValue) => {
    if (!draftPrefs) return;
    setDraftPrefs(applyFrequencyChange(draftPrefs, type, value));
    setSuccessMsg(null);
    setError(null);
  };

  const handleCancel = () => {
    if (savedPrefs) setDraftPrefs(savedPrefs);
    setError(null);
    setSuccessMsg(null);
  };

  const handleSave = async () => {
    if (!savedPrefs || !draftPrefs || !isDirty) return;

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      await saveEmailNotificationPreferences(draftPrefs);
      const refreshed = await fetchEmailNotificationPreferences();
      setSavedPrefs(refreshed);
      setDraftPrefs(refreshed);

      const changed = listChangedPreferenceTypes(savedPrefs, draftPrefs);
      setSuccessMsg(
        changed.length === 1
          ? t('views.accountSettings.notifications.savedOne', 'Notification preference saved.')
          : t('views.accountSettings.notifications.savedMany', {
              defaultValue: '{{count}} notification preferences saved.',
              count: changed.length,
            }),
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('views.accountSettings.notifications.saveError', 'Failed to save notification settings. Please try again.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const unsubErrorMessage = unsubError
    ? UNSUB_ERROR_KEYS[unsubError]
      ? t(UNSUB_ERROR_KEYS[unsubError], UNSUB_ERROR_DEFAULTS[unsubError])
      : t('views.accountSettings.notifications.unsub.unknown', 'We could not process your unsubscribe link.')
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="md" showText={false} />
      </div>
    );
  }

  if (!savedPrefs || !draftPrefs) {
    return (
      <div className="ds-card flex items-start gap-3 p-4" style={{ borderColor: 'var(--ds-danger-border)' }}>
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" style={{ color: 'var(--ds-danger)' }} />
        <div>
          <p className="t-body font-medium" style={{ color: 'var(--ds-text-strong)' }}>
            {t('views.accountSettings.notifications.loadFailed', 'Could not load notification settings')}
          </p>
          <p className="t-meta mt-1">{error ?? t('views.accountSettings.notifications.tryAgain', 'Please try again.')}</p>
          <button type="button" className="ds-btn ds-btn-secondary mt-3" onClick={() => void fetchPreferences({ showLoading: true })}>
            {t('views.accountSettings.notifications.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-28">
      <div
        className="rounded-xl border px-4 py-3"
        style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-recessed)' }}
      >
        <div className="flex items-start gap-3">
          <Bell className="mt-0.5 h-5 w-5 shrink-0" style={{ color: 'var(--ds-accent)' }} />
          <p className="t-body leading-relaxed" style={{ color: 'var(--ds-text-body)' }}>
            {t('views.accountSettings.notifications.introBefore', 'Choose which emails you want to receive, then click')}{' '}
            <strong>{t('views.accountSettings.notifications.saveChanges', 'Save changes')}</strong>.
            {t(
              'views.accountSettings.notifications.introAfter',
              ' These settings apply to scheduled emails and one-click unsubscribe. Transactional emails (payments, security, applications you submit) cannot be disabled.',
            )}
          </p>
        </div>
      </div>

      {unsubErrorMessage && (
        <div
          className="ds-card flex items-center gap-2 px-4 py-3 text-sm"
          style={{ borderColor: 'var(--ds-danger-border)', color: 'var(--ds-danger)' }}
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          {unsubErrorMessage}
        </div>
      )}

      {error && (
        <div
          className="ds-card flex items-center gap-2 px-4 py-3 text-sm"
          style={{ borderColor: 'var(--ds-danger-border)', color: 'var(--ds-danger)' }}
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {successMsg && (
        <div
          className="ds-card flex items-center gap-2 px-4 py-3 text-sm"
          style={{ borderColor: '#15803d', color: '#15803d' }}
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {successMsg}
        </div>
      )}

      {sections.map((section) => (
        <section
          key={section.id}
          className="overflow-hidden rounded-xl border"
          style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-card)' }}
        >
          <div
            className="border-b px-5 py-4"
            style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-recessed)' }}
          >
            <h3 className="t-section font-semibold" style={{ color: 'var(--ds-text-strong)' }}>
              {section.title}
            </h3>
            <p className="t-meta mt-1 leading-relaxed">{section.description}</p>
          </div>

          <div
            className="hidden grid-cols-[1fr_180px] gap-4 border-b px-5 py-2.5 sm:grid"
            style={{ borderColor: 'var(--ds-border-strong)' }}
          >
            <span className="t-meta font-medium uppercase tracking-wide">
              {t('views.accountSettings.notifications.emailType', 'Email type')}
            </span>
            <span className="t-meta font-medium uppercase tracking-wide">
              {t('views.accountSettings.notifications.frequencyLabel', 'Frequency')}
            </span>
          </div>

          {section.items.map((item, idx) => {
            const enabled = draftPrefs[item.type];
            const savedEnabled = savedPrefs[item.type];
            const frequency = enabledToFrequency(item, enabled);
            const isHighlighted = highlightType === item.type;
            const rowDirty = enabled !== savedEnabled;

            return (
              <div
                key={item.type}
                id={`email-pref-${item.type}`}
                className={`grid gap-3 border-b px-5 py-4 sm:grid-cols-[1fr_180px] sm:items-center ${
                  isHighlighted ? 'ring-2 ring-inset ring-[#15803d]/40' : ''
                }`}
                style={{
                  borderColor: idx === section.items.length - 1 ? 'transparent' : 'var(--ds-border-strong)',
                  background: isHighlighted ? 'rgba(21, 128, 61, 0.04)' : undefined,
                }}
              >
                <div>
                  <p className="t-body font-medium" style={{ color: 'var(--ds-text-strong)' }}>
                    {item.label}
                    {rowDirty && (
                      <span className="t-meta ml-2 font-normal" style={{ color: 'var(--ds-accent)' }}>
                        {t('views.accountSettings.notifications.unsaved', '(unsaved)')}
                      </span>
                    )}
                  </p>
                  {item.description && (
                    <p className="t-meta mt-1 leading-relaxed">{item.description}</p>
                  )}
                </div>

                <select
                  className="ds-field-input w-full min-w-0 rounded-lg border px-3 py-2.5 text-sm min-h-[44px] touch-manipulation"
                  style={{
                    borderColor: rowDirty ? 'var(--ds-accent)' : 'var(--ds-border-strong)',
                    background: 'var(--ds-bg-card)',
                    color: 'var(--ds-text-strong)',
                  }}
                  value={frequency}
                  disabled={saving}
                  onChange={(e) =>
                    handleDraftChange(item.type, e.target.value as FrequencyValue)
                  }
                  aria-label={t('views.accountSettings.notifications.frequencyAria', {
                    defaultValue: 'Frequency for {{label}}',
                    label: item.label,
                  })}
                >
                  {item.frequencies.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </section>
      ))}

      <section
        className="overflow-hidden rounded-xl border"
        style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-card)' }}
      >
        <div
          className="border-b px-5 py-4"
          style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-recessed)' }}
        >
          <h3 className="t-section font-semibold" style={{ color: 'var(--ds-text-strong)' }}>
            {t('views.accountSettings.notifications.transactionalTitle', 'Always on (transactional)')}
          </h3>
          <p className="t-meta mt-1 leading-relaxed">
            {t(
              'views.accountSettings.notifications.transactionalDesc',
              'These emails are required for your account and cannot be turned off.',
            )}
          </p>
        </div>
        <ul className="divide-y" style={{ borderColor: 'var(--ds-border-strong)' }}>
          {TRANSACTIONAL_EMAIL_KEYS.map((key, i) => (
            <li
              key={key}
              className="flex items-center justify-between gap-4 px-5 py-3.5"
              style={{ borderColor: 'var(--ds-border-strong)' }}
            >
              <span className="t-body" style={{ color: 'var(--ds-text-body)' }}>
                {t(key, TRANSACTIONAL_EMAIL_DEFAULTS[i])}
              </span>
              <span
                className="t-meta shrink-0 rounded-full px-2.5 py-1"
                style={{ background: 'var(--ds-bg-recessed)', color: 'var(--ds-text-muted)' }}
              >
                {t('views.accountSettings.notifications.required', 'Required')}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <div
        className="sticky bottom-0 z-10 mt-6 rounded-xl border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-5 py-3"
        style={{
          borderColor: isDirty ? 'var(--ds-accent)' : 'var(--ds-border-strong)',
          background: 'var(--ds-bg-card)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
        }}
      >
        <p className="t-meta hidden sm:block" style={{ color: 'var(--ds-text-muted)' }}>
          {isDirty
            ? t('views.accountSettings.notifications.unsavedChanges', 'You have unsaved changes.')
            : t('views.accountSettings.notifications.allSaved', 'All notification preferences are saved.')}
        </p>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            type="button"
            className="ds-btn ds-btn-secondary min-h-[44px] flex-1 sm:flex-initial sm:w-auto disabled:opacity-50 touch-manipulation"
            disabled={!isDirty || saving}
            onClick={handleCancel}
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-muted)',
              background: 'var(--ds-bg-card)',
            }}
          >
            {t('views.accountSettings.shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="ds-btn ds-btn-primary min-h-[44px] flex-1 sm:flex-initial sm:w-auto disabled:opacity-50 touch-manipulation"
            disabled={!isDirty || saving}
            onClick={() => void handleSave()}
          >
            {saving
              ? t('views.accountSettings.shared.saving', 'Saving…')
              : t('views.accountSettings.shared.saveChanges', 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsSettingsPanel;
